import React from "react";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { ImageWithFallback } from "@/Components/ImageFallback";
import { DEFAULT_COVER } from "@/constants/IMAGE_PATHS";
import { convertDraftToHtml } from "@/helper/helper";
import { getFeatureName } from "@/helper/featureNameHelper";
import { GA_EVENTS, trackProductEvent } from "@/util/analytics";

const sanitizeLinks = (html: string) =>
  html.replace(/<a\b[^>]*\bhref=["'][^"']*["'][^>]*>/g, "<a>");

interface TrainingBookCardProps {
  val: any;
  index: number;
  userRole: string;
  /** Pass true when rendered inside a responsive grid (fills full cell width) */
  isGrid?: boolean;
  /** Hide the Start/Continue button (e.g. the onboarding tour's demo card). */
  hideCta?: boolean;
}

export const TrainingBookCard: React.FC<TrainingBookCardProps> = ({
  val,
  index,
  userRole,
  isGrid = false,
  hideCta = false,
}) => {
  const router = useRouter();
  const { t } = useTranslation();

  const isUpcoming =
    val?.startDate && new Date(val.startDate) > new Date() ? true : false;

  // Hide upcoming trainings for regular users / managers
  if (isUpcoming && (userRole === "user" || userRole === "manager")) {
    return null;
  }

  const isExpired =
    val?.expiry && new Date(val.expiry) < new Date() ? true : false;
  const completionPercentage = Number(val?.completionPercentage || 0);
  const isTrainingInProgress =
    completionPercentage > 0 && completionPercentage < 100;

  const navigateToTraining = async () => {
    if (val?.onBoardId) {
      localStorage.setItem("onBoardingBatchId", val.onBoardId);
    }
    if (val?.learningPathId) {
      localStorage.setItem("learningPathId", val.learningPathId);
    }
    trackProductEvent(GA_EVENTS.APP_TRAINING_CARD_CLICKED, {
      training_name: val?.title,
      training_id: val?._id,
    });
    await router.push(
      { pathname: "/home/training", query: { id: val?._id } },
      undefined,
      { shallow: true }
    );
  };

  return (
    <div
      key={val?._id || index}
      // Onboarding tour spotlights the first real card (not its own demo card).
      data-tour={hideCta ? undefined : "training-card"}
      className={`stagger-card relative bg-cardBg border border-appText/10 rounded-2xl shadow overflow-hidden cursor-pointer flex flex-col ${
        isGrid ? "w-full h-full" : "flex-shrink-0 w-[320px] md:w-[360px]"
      }`}
      style={{ animationDelay: `${index * 100}ms` }}
      onClick={navigateToTraining}
    >
      {/* Expired overlay */}
      {isExpired && (
        <div className="absolute inset-0 bg-black/30 z-10 rounded-2xl pointer-events-none" />
      )}
      {isExpired && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cardBg text-[#FF4B55] text-base font-bold px-6 py-2 rounded-lg shadow-md z-50 whitespace-nowrap">
          {t("expired", "Expired")}
        </div>
      )}

      {/* Coming Soon overlay */}
      {!isExpired && isUpcoming && (
        <div className="absolute inset-0 bg-black/30 z-10 rounded-2xl pointer-events-none" />
      )}
      {!isExpired && isUpcoming && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cardBg text-[#FFA500] text-base font-bold px-6 py-2 rounded-lg shadow-md z-50 whitespace-nowrap">
          {t("upcoming", "Upcoming")}
        </div>
      )}

      {/* Cover image – full bleed */}
      <div className="relative h-[220px] w-full flex-shrink-0 bg-cardBg ">
        <ImageWithFallback
          src={val?.coverimage || DEFAULT_COVER}
          alt="book cover"
          fallbackSrc={DEFAULT_COVER}
          className="w-full object-contain h-56 border-b border-appText/20 "
        />
        {!val?.published && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cardBg text-appText text-sm font-bold px-6 py-2 rounded-lg shadow-md z-[100] whitespace-nowrap">
            {t("unpublished", "Unpublished")}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 px-4 py-3 flex-grow ">
        {/* Title + circular progress */}
        <div className="flex items-start justify-between gap-3 mt-2 ">
          <div className="flex-1 min-w-0">
            <p className="text-appText text-base font-bold leading-snug line-clamp-2">
              {val?.title}
            </p>
            <div className="min-h-[44px] mt-2">
              {val?.description && val?.description?.length > 0 ? (
                <div
                  className="text-sm text-appText/80 font-normal line-clamp-2 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeLinks(
                      convertDraftToHtml(val?.description || "")
                    ),
                  }}
                />
              ) : (
                <p className="text-sm text-appText/80 font-normal line-clamp-2 leading-relaxed">
                  {t("no_description_available", "No description available")}
                </p>
              )}
            </div>
          </div>

          {/* Circular progress ring */}
          {/* Circular progress ring */}
          {!isUpcoming && (
            <div className="flex-shrink-0 flex items-center justify-center w-[64px] h-[64px]">
              {(() => {
                const percentage = Number(val?.completionPercentage || 0);

                const getProgressColor = () => {
                  if (percentage <= 10) return "#ff006aff"; // Rose

                  if (percentage <= 50) return "#0066ffff"; // Amber

                  return "#16A34A"; // Emerald
                };

                const progressColor = getProgressColor();

                return (
                  <div className="relative flex items-center justify-center w-[64px] h-[64px]">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                      {/* Background */}
                      <path
                        stroke="#E5E7EB"
                        strokeWidth="3.5"
                        fill="none"
                        d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
                      />

                      {/* Progress */}
                      {percentage > 0 && (
                        <path
                          stroke={progressColor}
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={`${Math.min(percentage, 100)}, 100`}
                          d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
                        />
                      )}
                    </svg>

                    <span
                      className="absolute text-sm font-bold text-appText"
                      style={{}}
                    >
                      {percentage.toFixed(0)}%
                    </span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Start date pill */}
        {isUpcoming && val?.startDate && (
          <div className="flex items-center gap-2 border border-appText/20 rounded-md px-3 py-1 w-fit text-sm text-appText/70">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-[#4A4A4A]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-[#4A4A4A]">
              {t("starts_on", "Starts on")}&nbsp;–&nbsp;
              <span className="text-[#FF4B55] font-medium">
                {new Date(val.startDate).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </span>
          </div>
        )}

        {/* Expiry pill */}
        {val?.expiry && (
          <div className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-1 w-fit text-sm text-appText/80">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 appText/80 "
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-appText/80">
              {t("expires_on", "Expires on")}&nbsp;–&nbsp;
              <span className="text-appText/80 font-medium">
                {new Date(val.expiry).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </span>
          </div>
        )}

        {/* CTA button */}
        {!hideCta && (
          <button
            type="button"
            disabled={isExpired || isUpcoming}
            onClick={async (e) => {
              e.stopPropagation();
              await navigateToTraining();
              trackProductEvent(GA_EVENTS.APP_START_TRAINING_CLICKED, {
                training_name: val?.title,
                training_id: val?._id,
              });
              await router.push(
                { pathname: "/home/training", query: { id: val?._id } },
                undefined,
                { shallow: true }
              );
            }}
            className={`mt-auto w-full py-3 rounded-md text-sm font-semibold text-onPrimary transition-colors cursor-pointer ${
              isExpired
                ? "bg-appText/30 cursor-not-allowed"
                : "bg-primary hover:bg-primary/80"
            }`}
          >
            {isExpired
              ? t("expired", "Expired")
              : isUpcoming
                ? t("upcoming", "Upcoming")
                : isTrainingInProgress
                  ? t("continue", "Continue")
                  : t("start_training", "Start {{feature}}", {
                      feature: getFeatureName(t("training"), "training"),
                    })}
          </button>
        )}
      </div>
    </div>
  );
};
